import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";

const base = (campusId: number) => `/admin/campus/${campusId}/prospects`;

export interface ProspectFilters {
	view?: string | null;
	etapa?: number | null;
	q?: string;
	owner?: string | number | null;
	status?: number[];
	team?: string | null;
	objetivo?: string | null;
	turno?: string | null;
	curso?: string | null;
	flags?: string[];
}

export interface ProspectRow {
	id: number;
	phone_e164: string | null;
	first_name: string;
	last_name: string | null;
	etapa_id: number;
	status_id: number;
	status: string;
	potencial_motivo: string | null;
	course_interest_text: string | null;
	campus_id: number | null;
	source: string | null;
	ad_name: string | null;
	owner_zoho_name: string | null;
	owner_user_id: number | null;
	team_code: string | null;
	events_count: number;
	first_contact_at: string | null;
	last_inbound_at: string | null;
	last_outbound_at: string | null;
	last_activity_at: string | null;
	next_action_at: string | null;
	next_action_reason: string | null;
	student_user_id: number | null;
	last_message?: string | null;   // "kind|direction|texto"
	/** Matriculado: resumen de su matrícula en WAPP ("pagó 4 de 10 · PROMO · KIT"). */
	wapp?: RowWapp | null;
	gestiones?: { id: number; code: string; name: string; owner: string | null }[];
	objetivos?: string[];
}

export interface RowWapp {
	curso: string | null; modulo?: string | null; pagadas: number | null; meses: number | null; vencidas: number;
	promo: string | null; promo_ahorro: number | null; promo_paga: number | null; kit: "abono" | "pendiente" | null; campus_id?: number;
}

export interface Gestion {
	id: number; prospect_id: number; objetivo_id: number; objetivo: string; owner_user_id: number | null; owner_zoho_id: string | null; owner_name: string | null;
	status_id: number; next_action_at: string | null; next_action_reason: string | null; opened_at: string; opened_by: string; notes: string | null; enrollment_id: number | null;
}

export interface Objetivo { id: number; code: string; name: string; etapas: string; }
export interface Articulo { id: number; name: string; base_price: string | number; type_id: number; }
export interface PromoBulkPerson { id: number; name: string; curso: string; promo: string; params: string[]; }

export interface ProspectEvent {
	id: number;
	kind: "nota" | "llamada" | "whatsapp" | "tarea" | "cambio" | "asignacion" | "promo" | "sistema";
	direction: "in" | "out" | null;
	body: string | null;
	author_user_id: number | null;
	author_name: string | null;
	occurred_at: string;
	origen: "zoho" | "wapp" | "router" | "audit" | "vozmia";
	meta: any;
	hasRecording?: boolean;
	external_id?: string | null;
	channel_id?: number | null;
	wa_status?: "enviado" | "entregado" | "leido" | "fallido" | null;
}

export interface WaChannel { id: number; display_phone: string; phone_e164: string; name: string | null; label: string | null; country_id: number | null; campus_id: number | null; dept: string; }
export interface WaState { channelId: number | null; lastInboundAt: string | null; windowOpen: boolean; windowClosesAt: string | null; optOut: boolean; canSend: boolean; }
export interface WaTemplate { name: string; language: string; category: string | null; body: string; params: number; header: string | null; }
export interface DialerRow {
	id: number; phone_e164: string; first_name: string; last_name: string | null; status_id: number; status: string;
	course_interest_text: string | null; first_contact_at: string | null; last_inbound_at: string | null; next_action_at: string | null;
	next_action_reason: string | null; calls_count: number; call_noanswer_streak: number; last_call_at: string | null;
	last_call_result: string | null; wa_in_count: number; owner_zoho_name: string | null; score: number; reasons: string[];
}

export interface Paged<T> { data: T[]; total: number; page: number; perPage: number; }

export interface ProspectDetails {
	prospect: any;
	status: string;
	source: string | null;
	etapa: string | null;
	campuses: { id: number; name: string }[];
	zohoRecords: { zoho_module: string; zoho_id: string; zoho_created_at: string | null; zoho_modified_at: string | null }[];
	eventCounts: { kind: string; n: number; first_at: string }[];
	links: { id: number; kind: string; notes: string | null; prospect_id: number; first_name: string; last_name: string | null; phone_e164: string | null; etapa_id: number }[];
	gestiones: Gestion[];
	student: { id: number; first_name: string; last_name: string; identity_code: string; status_id: number } | null;
	/** Lo que WAPP sabe del alumno (matrícula, mensualidades, kit, retiro, promo, ofertas). Null si no es alumno. */
	wapp: WappContext | null;
}

export interface WappEnrollment {
	id: number; campus_id: number; status_id: number; estatus: string; created_at: string; updated_at: string; notes: string | null;
	schedule_id: number; horario: string; code: string; start_date: string | null; end_date: string | null; billable_months: number | null;
	monthly_billing_price: number | null; subscription_price: number | null; course_id: number; curso: string; level_id: number; modulo: string; maestro: string;
	pagadas: number | null; vencidas: number | null; total_mens: number | null; pagado_mens: number | null; saldo_vencido: number | null;
	proxima_fecha: string | null; proxima_monto: number | null; ultimo_mes_cursado: string | null; meses: number; termina_en_dias: number | null;
	asistencia: { pct: number; clases: number; faltas_seguidas: number } | null;
}
export interface WappKit { id: number; kit: string; sub_total: number; paid_amount: number; status_id: number; pendiente: number; estado: string; cancel_reason: string | null; }
export interface PromoEval { code: string; name: string; applies: boolean; why: string; payNow: number | null; saves: number; covers: number | null; }
export interface PromoResult { best: PromoEval | null; evaluated: PromoEval[]; remaining: number; listTotal: number; market?: string; reason?: string; }
export interface Oferta { kind: string; title: string; detail: string; action: string | null; }
export interface WappContext {
	student: { id: number; first_name: string; last_name: string; phone1: string | null; phone2: string | null; email: string | null; identity_code: string; status_id: number; status: string | null } | null;
	enrollments: WappEnrollment[]; active: WappEnrollment | null; kits: WappKit[];
	retiro: { enrollment_id: number; curso: string; modulo: string; fecha: string; motivo: string | null; ultima_nota: string | null; avanzo: number; meses: number; saldo: number; ultimo_mes_cursado: string | null; asesora: string | null } | null;
	etapa: number | null; reingreso: boolean; promo: PromoResult | null; ofertas: Oferta[];
}
export interface Quote {
	curso: string; modulo: string; moneda: string; simbolo: string; inscripcion: number; inscripcion_gratis_hasta: string; inscripcion_gratis_texto: string;
	mensualidad: number; meses: number; kit: { nombre: string; precio: number } | null; primera_mensualidad: number; hoy_paga: number; hoy_paga_detalle: string; promo: PromoResult | null;
}
export interface Tablero {
	mes: string; mesTexto: string; planteles: number[]; meta: number; matriculas: number; monto: number; creacionesHoy: number; faltaMeta: number; sinAsesora: number;
	equipos: { equipo: string; mat: number; monto: number; avance: number | null; falta: number }[];
	asesoras: { asesora: string; equipo: string; mat: number; monto: number }[];
	semanas: { semana: string; mat: number; monto: number }[];
	creaciones: { asesora: string; n: number; ultima: string }[];
	anuncios: { anuncio: string; red: string; leads: number; mat: number; conv: number }[];
	trimestre: { desde: string; matriculas: number; monto: number; equipos: { equipo: string; mat: number }[]; metaTrimestre: number };
}

export interface ProspectCatalogs {
	etapas: { [id: string]: string };
	statuses: { id: number; code: string; name: string; sort_order: number; bool_manual: number; bool_needs_reason: number }[];
	sources: { id: number; code: string; name: string }[];
	motivosPotencial: string[];
	linkKinds: string[];
	isSupervisor: boolean;
	advisors: { id: string; name: string; email: string | null; roles: string[]; team?: string }[];
	teams?: string[];
	objetivos?: Objetivo[];
	myZohoId?: string | null;
	campuses: { id: number; name: string; address?: string | null }[];
}

const qs = (o: { [k: string]: any }) =>
	Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && !v.length))
		.map(([k, v]) => Array.isArray(v) ? v.map((x) => `${k}[]=${encodeURIComponent(x)}`).join("&") : `${k}=${encodeURIComponent(v)}`)
		.join("&");

/** Ventas: bandeja, cartera, bitácora y alta manual (reemplazo del CRM de Zoho). */
export class ProspectsService {
	catalogs = async (campusId: number): Promise<AxiosResponse<ProspectCatalogs>> => axiosInstance.get(`${base(campusId)}/_catalogs`);

	views = async (campusId: number, f: ProspectFilters): Promise<AxiosResponse<{ counts: { [k: string]: number }; etapas: { [k: string]: number } }>> =>
		axiosInstance.get(`${base(campusId)}/_views?${qs({ ...f, view: undefined })}`);

	list = async (campusId: number, f: ProspectFilters, perPage: number, page: number): Promise<AxiosResponse<Paged<ProspectRow>>> =>
		axiosInstance.get(`${base(campusId)}?${qs({ ...f, perPage, page })}`);

	lookupPhone = async (campusId: number, phone: string): Promise<AxiosResponse<{ valid: boolean; phone: string | null; match: any }>> =>
		axiosInstance.get(`${base(campusId)}/_lookupPhone?phone=${encodeURIComponent(phone)}`);

	create = async (campusId: number, payload: any): Promise<AxiosResponse<ProspectDetails>> => axiosInstance.post(base(campusId), payload);

	details = async (campusId: number, id: number): Promise<AxiosResponse<ProspectDetails>> => axiosInstance.get(`${base(campusId)}/${id}/_details`);

	events = async (campusId: number, id: number, f: { kind?: string[]; q?: string }, perPage: number, page: number): Promise<AxiosResponse<Paged<ProspectEvent>>> =>
		axiosInstance.get(`${base(campusId)}/${id}/_events?${qs({ kind: f.kind, q: f.q, perPage, page })}`);

	note = async (campusId: number, id: number, body: string, kind: "nota" | "llamada" = "nota") =>
		axiosInstance.post(`${base(campusId)}/${id}/_note`, { body, kind });

	status = async (campusId: number, id: number, statusId: number, motivo?: string | null): Promise<AxiosResponse<ProspectDetails>> =>
		axiosInstance.put(`${base(campusId)}/${id}/_status`, { statusId, motivo });

	nextAction = async (campusId: number, id: number, at: string | null, reason?: string | null): Promise<AxiosResponse<ProspectDetails>> =>
		axiosInstance.put(`${base(campusId)}/${id}/_nextAction`, { at, reason });

	assign = async (campusId: number, id: number, advisorId: string, motivo?: string | null): Promise<AxiosResponse<ProspectDetails>> =>
		axiosInstance.put(`${base(campusId)}/${id}/_assign`, { advisorId, motivo });

	addLink = async (campusId: number, id: number, relatedId: number, kind: string, notes?: string | null) =>
		axiosInstance.post(`${base(campusId)}/${id}/_links`, { relatedId, kind, notes });

	removeLink = async (campusId: number, id: number, linkId: number) => axiosInstance.delete(`${base(campusId)}/${id}/_links/${linkId}`);

	/** Editar nombre, teléfonos, correo, curso, plantel, fuente. */
	update = async (campusId: number, id: number, data: any): Promise<AxiosResponse<ProspectDetails>> => axiosInstance.put(`${base(campusId)}/${id}`, data);

	whatsapp = async (campusId: number, id: number, channelId?: number | null): Promise<AxiosResponse<{ state: WaState; channels: WaChannel[] }>> =>
		axiosInstance.get(`${base(campusId)}/${id}/_whatsapp${channelId ? "?channelId=" + channelId : ""}`);

	waTemplates = async (campusId: number, channelId: number): Promise<AxiosResponse<{ templates: WaTemplate[] }>> =>
		axiosInstance.get(`${base(campusId)}/_waTemplates?channelId=${channelId}`);

	sendWhatsapp = async (campusId: number, id: number, payload: { text?: string; template?: string; language?: string; params?: string[]; channelId?: number | null }):
		Promise<AxiosResponse<{ event: ProspectEvent; state: WaState }>> => axiosInstance.post(`${base(campusId)}/${id}/_whatsapp`, payload);

	media = async (campusId: number, id: number, eventId: number): Promise<AxiosResponse<{ url: string | null }>> =>
		axiosInstance.get(`${base(campusId)}/${id}/_media/${eventId}`);

	dialer = async (campusId: number, f: ProspectFilters, limit = 50): Promise<AxiosResponse<{ total: number; queue: DialerRow[] }>> =>
		axiosInstance.get(`${base(campusId)}/_dialer?${qs({ ...f, view: undefined, q: undefined, limit })}`);

	waChannels = async (campusId: number): Promise<AxiosResponse<{ channels: (WaChannel & { bool_enabled: number; mensajes: number | null; ultimo: string | null })[] }>> =>
		axiosInstance.get(`${base(campusId)}/_waChannels`);

	updateWaChannel = async (campusId: number, channelId: number, data: { label?: string; campusId?: number | null; dept?: string; enabled?: boolean; route?: string | null }) =>
		axiosInstance.put(`${base(campusId)}/_waChannels/${channelId}`, data);

	rotation = async (campusId: number): Promise<AxiosResponse<{ autoassign: boolean; routes: { code: string; name: string; enabled: boolean; roles: string[]; channels: string[]; advisors: { id: string; name: string; role: string; inRotation: number }[] }[] }>> =>
		axiosInstance.get(`${base(campusId)}/_rotation`);

	setInRotation = async (campusId: number, advisorId: string, inRotation: boolean) =>
		axiosInstance.put(`${base(campusId)}/_rotation/${advisorId}`, { inRotation });

	assignBulk = async (campusId: number, ids: number[], advisorId: string, motivo?: string | null): Promise<AxiosResponse<{ reasignados: number; fallas: any[] }>> =>
		axiosInstance.put(`${base(campusId)}/_assignBulk`, { ids, advisorId, motivo });

	/** Descarga la lista filtrada como CSV (se abre en Excel). */
	export = async (campusId: number, f: ProspectFilters): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(`${base(campusId)}/_export?${qs({ ...f })}`, { responseType: "blob" });

	quote = async (campusId: number, course: string): Promise<AxiosResponse<{ quote: Quote | null }>> =>
		axiosInstance.get(`${base(campusId)}/_quote?course=${encodeURIComponent(course)}`);

	dashboard = async (campusId: number, month?: string | null, scope?: string | null): Promise<AxiosResponse<Tablero>> =>
		axiosInstance.get(`${base(campusId)}/_dashboard?${qs({ month, scope })}`);

	call = async (campusId: number, id: number, data: { result: string; note?: string; durationSec?: number; source?: string; laterMinutes?: number }) =>
		axiosInstance.post(`${base(campusId)}/${id}/_call`, data);
	cartera = async (campusId: number, f: ProspectFilters): Promise<AxiosResponse<{ total: number; etapas: { [k: string]: number }; sin_tocar_30: number }>> =>
		axiosInstance.get(`${base(campusId)}/_cartera?${qs({ ...f, view: undefined })}`);
	openGestion = async (campusId: number, id: number, data: { objetivo: string; ownerZohoId?: string | null; nextActionAt?: string | null; reason?: string | null }):
		Promise<AxiosResponse<ProspectDetails>> => axiosInstance.post(`${base(campusId)}/${id}/_gestiones`, data);
	closeGestion = async (campusId: number, id: number, gestionId: number, data: { outcome: string; notes?: string | null }): Promise<AxiosResponse<ProspectDetails>> =>
		axiosInstance.put(`${base(campusId)}/${id}/_gestiones/${gestionId}`, data);
	vitrina = async (campusId: number, q?: string | null): Promise<AxiosResponse<Articulo[]>> =>
		axiosInstance.get(`${base(campusId)}/_vitrina?${qs({ q })}`);
	promoBulkPreview = async (campusId: number, f: ProspectFilters): Promise<AxiosResponse<{ count: number; people: PromoBulkPerson[] }>> =>
		axiosInstance.get(`${base(campusId)}/_promoBulk?${qs({ ...f, view: undefined })}`);
	promoBulkSend = async (campusId: number, items: { id: number; params: string[] }[], template: string, language: string | null, channelId: number | null):
		Promise<AxiosResponse<{ results: { id: number; ok: boolean; error?: string }[] }>> =>
		axiosInstance.post(`${base(campusId)}/_promoBulk`, { items, template, language, channelId });
}
